import { API_BASE_URL } from "../constants";
import { apiFetch, apiRequest } from "./api.client";

export { ApiError as ImmunizationApiError } from "./api.errors";

// ── Types ──────────────────────────────────────────────────────────────────────

export type ScheduleSource = "NIS" | "IAP";
export type DisplayStatus = "administered" | "skipped" | "overdue" | "blocked" | "due";

export interface ImmunizationDose {
  dose_id: string;
  patient_id: string;
  vaccine_id: string;
  vaccine_code: string;
  vaccine_name: string;
  schedule_source: ScheduleSource;
  series_key: string;
  dose_number: number;
  due_date: string;
  display_status: DisplayStatus;
  blocking_reasons: string[];
  administered_date?: string | null;
  administered_by?: string | null;
  batch_no?: string | null;
  override_reason?: string | null;
  overridden_by?: string | null;
}

export interface ImmunizationCalendar {
  patient_id: string;
  doses: ImmunizationDose[];
}

export interface AdministerDosePayload {
  administered_date: string;
  batch_no?: string;
  override_reason?: string;
}

export interface CohortEntry {
  patient_id: string;
  patient_name: string;
  date_of_birth: string;
  dose_id: string;
  dose_number: number;
  vaccine_code: string;
  vaccine_name: string;
  due_date: string;
  guardian_whatsapp_number?: string | null;
  consented: boolean;
}

export interface CohortResult {
  missed_since: string;
  entries: CohortEntry[];
  total_missed: number;
  total_consented: number;
}

export interface CohortRemindResult {
  attempted: number;
  sent: number;
  skipped_no_consent: number;
  skipped_quiet_hours: number;
  skipped_insufficient_credits: number;
}

export interface CohortOutcomeResult {
  total_reminded: number;
  administered_after: number;
  still_pending: number;
}

// A 422 dose-block from the rule engine - distinct from a thrown ApiError so
// callers can render the specific blocking-reason chips instead of a generic
// error toast. Mirrors how sync-push.engine.ts reads the "detail" wrapper
// directly rather than through apiRequest's generic unwrap.
export interface DoseBlockedResult {
  blocked: true;
  reasons: string[];
}

export const ImmunizationAPI = {
  getCalendar: (patientId: string) =>
    apiRequest<ImmunizationCalendar>(`/patients/${patientId}/immunizations`),

  administerDose: async (
    patientId: string,
    doseId: string,
    payload: AdministerDosePayload
  ): Promise<ImmunizationDose | DoseBlockedResult> => {
    // apiFetch, not bare fetch: the dose must be recorded in the clinic the
    // owner is working in (X-Active-Clinic), and doctors need X-Device-Id.
    const resp = await apiFetch(
      `${API_BASE_URL}/patients/${patientId}/immunizations/${doseId}/administer`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        signal: AbortSignal.timeout(30_000),
      }
    );
    if (resp.status === 422) {
      const body = await resp.json().catch(() => ({}));
      if (body?.detail?.error === "dose_blocked") {
        return { blocked: true, reasons: body.detail.reasons ?? [] };
      }
    }
    if (!resp.ok) {
      const body = await resp.json().catch(() => ({}));
      throw new Error(body?.error?.message || body?.detail?.message || `HTTP ${resp.status}`);
    }
    const data = await resp.json();
    return data.data as ImmunizationDose;
  },

  catchUp: (patientId: string, doseId: string, administeredDate?: string) =>
    apiRequest<ImmunizationDose>(`/patients/${patientId}/immunizations/${doseId}/catch-up`, {
      method: "POST",
      body: JSON.stringify(administeredDate ? { administered_date: administeredDate } : {}),
    }),

  getCohort: (clinicId: string, missedSince = "6m") =>
    apiRequest<CohortResult>(`/clinics/${clinicId}/immunizations/cohort?missed_since=${missedSince}`),

  remindCohort: (clinicId: string, missedSince = "6m") =>
    apiRequest<CohortRemindResult>(
      `/clinics/${clinicId}/immunizations/cohort/remind?missed_since=${missedSince}`,
      { method: "POST" }
    ),

  getCohortOutcomes: (clinicId: string) =>
    apiRequest<CohortOutcomeResult>(`/clinics/${clinicId}/immunizations/cohort/outcomes`),
};

export function isDoseBlocked(result: ImmunizationDose | DoseBlockedResult): result is DoseBlockedResult {
  return (result as DoseBlockedResult).blocked === true;
}
